import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './AdminEditOurTeam.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import publicAxios from '../../utils/axios/publicAxios';
import Notification from '../../components/Notification/Notification';
import { formats, modules, memberCategories } from '../../assets/exports';
import { jwtDecode } from 'jwt-decode';

const AdminEditOurTeam = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ Decode user info from token (like CreatePost)
  const [decodedData, setDecodedData] = useState({});
  const userRoles = decodedData?.userRoles || [];

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [display, setDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  // ✅ Notification handler
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  // ✅ Decode token and fetch post
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedData(decoded);
      } catch (err) {
        console.error('Invalid token:', err);
        navigate('/login');
      }
    }
  }, [navigate]);

  // ✅ Fetch the post data
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const res = await privateAxios.get(`/private/fetch-admin-edit-our-team/${id}`);
        const { title, content, imageUrl, display, category } = res.data.post;

        setTitle(title);
        setContent(content);
        setImageUrl(imageUrl);
        setOriginalImageUrl(imageUrl);
        setDisplay(display);
        setCategory(category || userRoles[0] || '');
      } catch (err) {
        console.error('Failed to load post:', err);
        showNotification('error', 'Failed to load post.');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id, userRoles]);

  // ✅ Image handling
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };

  // ✅ S3 upload logic (same as CreatePost)
  const uploadImageToS3 = async () => {
    if (!imageFile) return originalImageUrl;

    try {
      const res = await privateAxios.get('/private/generate-upload-url', {
        params: {
          filename: imageFile.name,
          filetype: imageFile.type,
        },
      });

      const { url } = res.data;
      await publicAxios.put(url, imageFile);
      return url.split('?')[0];
    } catch (error) {
      console.error('Image upload failed:', error);
      throw new Error('Failed to upload image.');
    }
  };

  // ✅ Update handler
  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      let updatedImageUrl = originalImageUrl;
      if (imageFile) {
        updatedImageUrl = await uploadImageToS3();
      }

      await privateAxios.patch(`/private/admin-edit-our-team/${id}`, {
        title,
        content,
        category,
        imageUrl: updatedImageUrl,
        display,
      });

      showNotification('success', 'Post updated successfully!');
      setTimeout(() => navigate('/admin-our-team'), 3000);
    } catch (error) {
      console.error('Failed to update post:', error);
      showNotification('error', error.message || 'Failed to update post.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="admin-edit-post">
      <Notification
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ type: '', message: '' })}
      />

      <Link to="/admin-posts" className="edit-post-back">
        Back to Posts
      </Link>

      <h2>Edit Post</h2>

      {loading ? (
        <div className="admin-edit-post-loading-container">
          <Circles height="50" width="50" color="#008751" ariaLabel="loading" />
          <h2>Loading Team Member...</h2>
        </div>
      ) : (
        <form onSubmit={handleUpdate} className="edit-form">
          <label>Member Name:</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />

          <label>Bio (optional):</label>
          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            theme="snow"
            className="quill-editor"
          />

          <label>Category:</label>
          <select
            className="edit-post-select-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            required
          >
            {memberCategories.map((role) => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>

          {userRoles.includes('admin') && (
            <div className="admin-edit-our-team-display-checkbox">
              <label>
                <input type="checkbox" className='admin-edit-our-team-checkbox' checked={display} onChange={(e) => setDisplay(e.target.checked)} />
                <p>Display publicly</p>
              </label>
            </div>
          )}

          <label>Change Image (optional):</label>
          <input type="file" accept="image/*" style={{ margin: 0, padding: 0 }} onChange={handleImageChange} />
          {imageUrl && (
            <div className="admin-edit-post-image-div">
              <img src={imageUrl} alt="Preview" className="image-preview-image" />
            </div>
          )}

          <div className="edit-actions">
            <button type="submit" disabled={updating}>
              {updating ? 'Updating...' : 'Save Changes'}
            </button>
            <button type="button" onClick={() => navigate('/admin-our-team')}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminEditOurTeam;